import { useMemo, type ComponentProps, type ReactNode } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import Table, {
  type ColumnMetaMap,
  type TableProps,
  type PageInfo,
  createColumns,
  GROUPING_RANGE_DATES,
} from "@/v2-backoffice-client/components/table"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import UserLoginRevokeAction from "@/v2-backoffice-client/userLogin/revoke/UserLoginRevokeAction"
import type { UserLoginRow } from "@/v2-backoffice-client/userLogin/list/userLogin.list.query"

function formatLocation(row: UserLoginRow): string | null {
  const parts = [row.IpCity, row.IpCountry].filter(Boolean)
  return parts.length > 0 ? parts.join(", ") : null
}

function createSessionColumns(hideUser: boolean) {
  const columns: ColumnMetaMap<UserLoginRow> = {}

  if (!hideUser) {
    columns.UserName = {
      id: "UserName",
      label: "User",
      enableGrouping: true,
      getValue: (row) => formatUserName(row.FirstName, row.LastName),
      to: ({ row }) => {
        if (!row.original?.UserId) return
        return `>>?v=user:${row.original.UserId}`
      },
    }
  }

  columns.Status = {
    id: "Status",
    label: "Status",
    description:
      "Whether this login is still live or has been revoked (by a takeover, a logout, or an operator).",
    defaultSize: 90,
    enableGrouping: true,
    filter: {
      type: "select",
      placeholder: "Any status",
      toolbar: true,
      options: [
        { label: "Live", value: "live", dotVariant: "success-1" },
        { label: "Revoked", value: "revoked", dotVariant: "secondary" },
      ],
    },
    getValue: (row) => (row.IsLive ? "live" : "revoked"),
    renderCell({ row }) {
      return row.original.IsLive ? (
        <DotText variant="success-1">Live</DotText>
      ) : (
        <DotText variant="secondary">Revoked</DotText>
      )
    },
    exportValue({ row }) {
      return row.original.IsLive ? "Live" : "Revoked"
    },
  }

  columns.Team = {
    id: "Team",
    label: "Team",
    description: "The team(s) the signed-in user belongs to.",
    enableGrouping: true,
    getValue: (row) => row.TenantNames ?? "",
    renderCell({ row }) {
      if (!row.original.Tenants.length) return null
      return (
        <TruncatedList
          items={row.original.Tenants.map((tenant) => ({
            label: tenant.Name,
            to: `>>?v=tenant:${tenant.Id}`,
          }))}
        />
      )
    },
    exportValue({ row }) {
      return row.original.TenantNames ?? ""
    },
  }

  columns.DeviceLabel = {
    id: "DeviceLabel",
    label: "Device",
    description: "Browser and OS parsed from the login's user agent.",
    enableGrouping: true,
    getValue: (row) => row.DeviceLabel ?? "",
    renderCell({ row }) {
      return row.original.DeviceLabel ?? null
    },
    exportValue({ row }) {
      return row.original.DeviceLabel ?? ""
    },
  }

  columns.Location = {
    id: "Location",
    label: "Location",
    description: "City and country estimated from the sign-in IP address.",
    enableGrouping: true,
    getValue: (row) => formatLocation(row) ?? "",
    renderCell({ row }) {
      return formatLocation(row.original)
    },
    exportValue({ row }) {
      return formatLocation(row.original) ?? ""
    },
  }

  columns.AppVersion = {
    id: "AppVersion",
    label: "Version",
    description:
      "The app (cohort) version last seen from this device. Empty for logins from before versions were tracked.",
    defaultSize: 100,
    enableGrouping: true,
    getValue: (row) => row.AppVersion ?? "",
    renderCell({ row }) {
      if (!row.original.AppVersion) return null
      return (
        <span className="font-mono text-xs">{row.original.AppVersion}</span>
      )
    },
    exportValue({ row }) {
      return row.original.AppVersion ?? ""
    },
  }

  columns.CreatedAt = {
    id: "CreatedAt",
    label: "Signed in",
    defaultSize: 100,
    groupingRanges: GROUPING_RANGE_DATES,
    renderCell({ row }) {
      return <RelativeDate date={row.original.CreatedAt} />
    },
    exportValue({ row }) {
      return formatDateString(row.original.CreatedAt)
    },
  }

  columns.LastSeenAt = {
    id: "LastSeenAt",
    label: "Last seen",
    defaultSize: 100,
    groupingRanges: GROUPING_RANGE_DATES,
    renderCell({ row }) {
      return <RelativeDate date={row.original.LastSeenAt} />
    },
    exportValue({ row }) {
      return formatDateString(row.original.LastSeenAt)
    },
  }

  columns.RevokedAt = {
    id: "RevokedAt",
    label: "Revoked",
    defaultHidden: true,
    defaultSize: 100,
    groupingRanges: GROUPING_RANGE_DATES,
    renderCell({ row }) {
      const revokedAt = row.original.RevokedAt
      return revokedAt ? <RelativeDate date={revokedAt} /> : null
    },
    exportValue({ row }) {
      const revokedAt = row.original.RevokedAt
      return revokedAt ? formatDateString(revokedAt) : ""
    },
  }

  columns.IpAddress = {
    id: "IpAddress",
    label: "IP Address",
    defaultHidden: true,
    renderCell({ row }) {
      return row.original.IpAddress ?? null
    },
  }

  columns.Email = {
    id: "Email",
    label: "Email",
    defaultHidden: true,
    renderCell({ row }) {
      return row.original.Email ?? null
    },
  }

  return createColumns<UserLoginRow>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns,
  })
}

export type UserLoginTableProps = Omit<
  TableProps<UserLoginRow>,
  "data" | "columns"
> &
  ComponentProps<"div"> & {
    logins: UserLoginRow[]
    isLoading?: boolean
    error?: Error
    pageInfo?: PageInfo | null
    onFetchMore?(): void
    onRefresh?(): void
    toolbarExtra?: ReactNode
    /** Hide the User column + name search (per-user panel, where it's redundant). */
    hideUser?: boolean
  }

function UserLoginTable(props: UserLoginTableProps) {
  const {
    className,
    logins,
    isLoading,
    error,
    pageInfo,
    onFetchMore,
    onRefresh,
    toolbarExtra,
    hideUser = false,
    ...tableProps
  } = props

  const columns = useMemo(() => createSessionColumns(hideUser), [hideUser])

  return (
    <Table<UserLoginRow>
      className={cn("user-login-table", className)}
      tableId="user-login-table"
      data={logins}
      columns={columns}
      enableGrouping
      enableRowSelection
      getRowId={(row) => row.LoginId}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        {!hideUser && <Table.Search placeholder="Search by user…" />}
        {toolbarExtra}
        <Table.ToolbarFilters />

        <Separator orientation="vertical" />
        <Table.ToolbarMenu
          onRefresh={onRefresh}
          exportFileName="active-logins"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />
      <Table.Content
        countLabel="active logins"
        isLoading={isLoading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
      />

      <Table.SelectionToolbar<UserLoginRow>>
        {(selected, reset) => (
          <UserLoginRevokeAction
            selectedRows={selected}
            onComplete={() => {
              reset()
              onRefresh?.()
            }}
          />
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default UserLoginTable
